import React, { useState } from "react";
import AlertComponent from "components/Alert";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useInitiateDisbursementMutation } from "redux/services/disbursement";
import type { DisbursementErrorResponse } from "redux/services/disbursement/types";
import { safeParseJSON } from "utils";

interface Props {
  isOpen: boolean;
  approvalTask?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const DisburseConfirmationDialog = ({ isOpen, approvalTask, onClose, onSuccess }: Props) => {
  const toast = useToast();
  const cancelRef = React.useRef(null);
  const [commentValue, setCommentValue] = useState<string>("");

  const [initiateDisbursement, { isLoading, isError, error }] = useInitiateDisbursementMutation();

  const processVariables = approvalTask?.processVariables
    ? safeParseJSON(approvalTask.processVariables.overallInformation)
    : {};

  const handleClose = () => {
    setCommentValue("");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await initiateDisbursement({
        file_name: processVariables.payrollDocumentName,
      }).unwrap();

      // Show success toast
      toast({
        title: "Disbursement Initiated Successfully",
        description: `The disbursement process has been initiated for file: ${processVariables.payrollDocumentName}`,
        status: "success",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });

      // Call parent callbacks
      onSuccess?.();
      handleClose();
    } catch (error) {
      // Show error toast
      const errorData = error as DisbursementErrorResponse;
      toast({
        title: errorData?.title || "Disbursement Failed",
        description: errorData?.description || "An error occurred while initiating disbursement.",
        status: "error",
        duration: 9000,
        position: "top-right",
        isClosable: true,
      });
      // Alert component in JSX will still show error persistently
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={isLoading ? () => {} : handleClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Disburse Payment
          </AlertDialogHeader>

          <AlertDialogBody>
            {isError && error && (
              <AlertComponent
                data-testid="alert-error"
                status="error"
                mb="10px"
                title={(error as DisbursementErrorResponse)?.title || "Disbursement Failed"}
                description={
                  (error as DisbursementErrorResponse)?.description ||
                  "An error occurred while initiating disbursement."
                }
              />
            )}

            {commentValue && commentValue.length >= 250 && (
              <AlertComponent
                data-testid="alert-error"
                status="error"
                mb="10px"
                title="Comment must be less than 250 characters"
              />
            )}

            <Text fontSize="14px" lineHeight="1.5">
              Are you sure you want to disburse this payment? This action will initiate the
              disbursement process and cannot be undone.
            </Text>

            <Textarea
              mt="16px"
              fontSize="14px"
              placeholder="Add a comment"
              value={commentValue}
              onChange={(e) => setCommentValue(e.target.value)}
              isInvalid={!!(commentValue && commentValue?.length >= 250)}
            />
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              ml={3}
              disabled={isLoading || !!(commentValue && commentValue?.length >= 250)}
              isLoading={isLoading}
            >
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DisburseConfirmationDialog;
